import { useState } from 'react';
import {
  FileTextOutlined,
  DeleteOutlined,
  WarningOutlined,
  LoadingOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Input, Tooltip } from 'antd';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';
import type { PatientDocument } from 'src/models';
import { useUpdatePatientDocument } from 'src/hooks/patient';
import { Modal, Button, toast } from '@insight-health-ai/roseate/components';
import classNames from 'classnames';

export type PatientFileDocumentProps = {
  isLoading?: boolean;
  hasError?: boolean;
  /**
   * Only required for events
   */
  source: 'vca_patient_history' | 'pre_chart_history';
  document: PatientDocument;
  canEdit?: boolean;
  canDelete?: boolean;
  onUpdate?: (attachment: {
    document: PatientDocument;
    fileToUpload?: File | null;
  }) => void;
  onDelete?: (attachment: { document: PatientDocument }) => void;
  // @todo: Harshit | remove events responsibility from this component
  sendEvents?: boolean;
};

export const PatientFileDocument = ({
  document,
  onUpdate,
  onDelete,
  source,
  isLoading: isLoadingProp,
  hasError,
  canEdit = true,
  canDelete = true,
  sendEvents = false,
}: PatientFileDocumentProps) => {
  const { user } = useAuth();

  const [description, setDescription] = useState(document.description || '');
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const {
    mutateAsync: updatePatientDocument,
    isPending: isUpdatingPatientDocument,
  } = useUpdatePatientDocument();

  const isLoading = isLoadingProp || isUpdatingPatientDocument;

  const updateDescription = async () => {
    try {
      const updatedDocument = await updatePatientDocument({
        patientDocumentId: document.patient_document_id,
        description,
      });
      onUpdate?.({ document: updatedDocument });
      setShowDescriptionModal(false);
    } catch (error) {
      toast.error('Failed to update description');
    }
  };

  const prefixIcon = isLoading ? (
    <LoadingOutlined />
  ) : hasError ? (
    <WarningOutlined />
  ) : (
    <FileTextOutlined />
  );
  return (
    <div className="flex flex-col items-center group rounded-lg max-w-[160px]">
      {showDescriptionModal && (
        <Modal
          centered
          open
          onClose={() => setShowDescriptionModal(false)}
          primaryAction={{
            label: 'Save',
            onClick: async () => {
              if (sendEvents) {
                analytics.track(
                  `Clicked Save In ${
                    source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                  } History Edit Description Modal`,
                  {
                    email: user?.email,
                  }
                );
              }
              await updateDescription();
            },
          }}
          secondaryAction={{
            label: 'Cancel',
            onClick: async () => {
              if (sendEvents) {
                analytics.track(
                  `Clicked Cancel In ${
                    source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                  } History Edit Description Modal`,
                  {
                    email: user?.email,
                  }
                );
              }
              setDescription(document.description || '');
              await setShowDescriptionModal(false);
            },
          }}
          extras={
            canDelete && (
              <Button
                appearance="danger"
                onClick={async () => {
                  if (sendEvents) {
                    analytics.track(
                      `Deleted File In ${
                        source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                      } History Edit Description Modal`,
                      {
                        email: user?.email,
                      }
                    );
                  }
                  await onDelete?.({ document });
                  setShowDescriptionModal(false);
                }}
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            )
          }
          title="Edit Description"
        >
          <Input.TextArea
            autoFocus
            style={{ resize: 'none' }}
            rows={4}
            defaultValue={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Modal>
      )}
      <div
        className={classNames('relative', {
          'cursor-pointer': canEdit,
        })}
        onClick={() => canEdit && setShowDescriptionModal(true)}
      >
        <span className={`text-[40px] ${hasError ? 'text-red-500' : ''}`}>
          {prefixIcon}
        </span>
        <div className="absolute -bottom-1 p-1 flex gap-2 justify-center left-1/2 -translate-x-1/2">
          {canEdit && (
            <Tooltip title="Edit Description">
              <button
                disabled={isLoading || hasError}
                className="flex rounded-full p-1 bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
                aria-label="Edit Description"
              >
                <EditOutlined className="text-md text-gray-600" />
              </button>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <button
                disabled={isLoading}
                className="flex rounded-full p-1 bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
                onClick={async (e) => {
                  if (sendEvents) {
                    analytics.track(
                      `Deleted File In ${
                        source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                      } History`,
                      {
                        email: user?.email,
                      }
                    );
                  }
                  e.stopPropagation();
                  await onDelete?.({ document });
                }}
                aria-label="Delete file"
              >
                <DeleteOutlined className="text-md text-gray-600" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="w-full text-center">
        <p
          className={`text-xs font-medium text-gray-900 truncate px-1 ${
            hasError && !isLoading ? 'text-red-500' : ''
          }`}
          title={document.name || 'Document'}
        >
          {document.name || 'Document'}
        </p>
        <p className="text-xs text-gray-500">
          {((document.size || 0) / 1000).toFixed(2)} KB
        </p>
      </div>
    </div>
  );
};
